import * as React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';
import { Typography } from '@material-ui/core';

import { TaskContent } from '../../youwell-common/components';
import TaskScript from './TaskScript';
import { parseInternalLink } from '../../youwell-common/taskContentUtils';
import urls from '../../app/urls';

import * as PatientStore from '../../app/store/PatientStore';
import { isBrowser } from '../../youwell-common/browserUtils';

export const AUTOSAVE_TIMEOUT_INTERVAL = 1000; // miliseconds

export const styles = theme => ({
    root: {
    },
    noContent: {
        padding: theme.spacing(4),
        margin: theme.spacing(2),
    },
    htmlContent: {
        color: theme.colors.blue4,
    },
    elementRoot: {

    },
    elementContent: {

    },
    elementHeader: {
        background: theme.colors.grey2,
        padding: theme.spacing(1, 2),
    },
});

const useStyles = makeStyles(theme => styles(theme), { name: 'VimoTaskContent' });

const handleScripts = (element) => {
    if (element.script) {
        return (<TaskScript script={element.script} />);
    }
    return null;
};

export const getContentTranslations = translate => ({
    show: translate('common.show'),
    hide: translate('common.hide'),
    close: translate('common.close'),
    openFile: translate('common.openFile'),
    openLinkInNewWindow: translate('common.openLinkInNewWindow'),
    saveResponses: translate('common.save'),
    answerAllRequiredQuestions: translate('taskContent.pleaseAnswerRequiredQuestions'),
    freetextPlaceholder: translate('taskContent.writeYourResponseHere'),
    unsavedChangesPrompt: translate('taskContent.unsavedChangesPrompt'),
    allChangesSaved: translate('taskContent.allChangesSaved'),
    freetextChoice: translate('taskContent.freetextChoice'),
    youGotxOfXScore: p => translate('taskContent.youGotxOfXScore', p),
    maxLengthXofY: p => translate('taskContent.maxLengthXofY', p),
    freetextChoiceLabel: translate('taskContent.freetextChoiceLabel'),
});

const VimoTaskContent = ({
    task, hidePreviousResponses, readOnly, className, forwardedRef, onSaveChanges, onUpdateResponse, responses,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const translate = useSelector(state => getTranslate(state.localize));
    const allContent = useSelector(state => state.program.programDetails);

    const getLinkUrl = (link) => {
        const { taskOrModuleId, hash } = parseInternalLink(link);
        return `${urls.inside.resource.task}/${taskOrModuleId}${hash}`;
    };

    const getProgramItem = id => (allContent ? allContent[id] : null);

    const clickInternalLink = React.useCallback((elementId) => {
        const url = getLinkUrl({ url: elementId });
        dispatch(PatientStore.actionCreators.redirect(url));
    }, [dispatch]);

    const noAction = () => {};

    React.useEffect(() => {
        if (isBrowser) {
            // eslint-disable-next-line no-underscore-dangle
            window.__clickInternalLink = clickInternalLink;
        }

        return () => {
            if (isBrowser) {
            // eslint-disable-next-line no-underscore-dangle
                window.__clickInternalLink = null;
            }
        };
    }, [clickInternalLink]);

    return (
        <TaskContent
            ref={forwardedRef}
            className={classnames(classes.root, className)}
            TaskElementProps={{
                classes: {
                    header: classes.elementHeader,
                    htmlContent: classes.htmlContent,
                    root: classes.elementRoot,
                    content: classes.elementContent,
                },
                customElementHandler: handleScripts,
            }}
            TaskFormProps={{
                hidePreviousResponses,
                readOnly,
            }}
            LinkElementProps={{
                getLinkUrl,
            }}
            task={task}
            noContent={(
                <Typography align="center" color="textSecondary" className={classes.noContent}>
                    {translate('task.noContent')}
                </Typography>
            )}
            getProgramItem={getProgramItem}
            getSpecificTaskResponses={() => {}}
            translations={getContentTranslations(translate)}
            taskResponses={responses}
            onResponse={onUpdateResponse || noAction}
            onSaveChanges={onSaveChanges || noAction}
        />
    );
};

VimoTaskContent.propTypes = {
    task: PropTypes.object.isRequired,
    forwardedRef: PropTypes.object,
    hidePreviousResponses: PropTypes.bool,
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    onSaveChanges: PropTypes.func,
    onUpdateResponse: PropTypes.func,
    responses: PropTypes.object,
};

VimoTaskContent.defaultProps = {
    forwardedRef: null,
    hidePreviousResponses: false,
    readOnly: false,
    className: null,
    onSaveChanges: null,
    onUpdateResponse: null,
    responses: null,
};

const WithRef = React.forwardRef((props, ref) => <VimoTaskContent {...props} forwardedRef={ref} />);
export default WithRef;
